import React, { useState, useCallback, useMemo } from 'react'
import { useTranslation } from 'i18n'
import { DropdownSelect as UntypedDropdownSelect } from '../../components/DropDownSelect'
import { useOfferTags } from './useOfferTags'
import { usePromoItems } from './usePromoItems'
import OfferCard from './OfferCard'
// @ts-expect-error SCSS modules are untyped in this project
import * as styles from './promo-page.module.scss'

const DropdownSelect = UntypedDropdownSelect as unknown as React.ComponentType<{
  title: string
  dropdownItems: unknown[]
  onSelect: (text: string, title: string) => void
  singleSelect?: boolean
  clearSelect?: string
}>

const SORT_PARAM = 'sort'
const TAGS_PARAM = 'tags'

// At or below this count the cards render in the wide layout.
const WIDE_LAYOUT_MAX_ITEMS = 5

// Labels are translated, so the list is built inside the component (below).
const SORTING_PARAMS = [
  { id: '0', labelKey: 'sort.alphabeticalAsc', param: 'headline' },
  { id: '1', labelKey: 'sort.alphabeticalDesc', param: 'headline,DESC' },
  { id: '2', labelKey: 'sort.endDateSoonest', param: 'end' },
  { id: '3', labelKey: 'sort.endDateLatest', param: 'end,DESC' },
]

type PromoPageStepProps = {
  step?: {
    stepId?: number | string
    originalStepId?: number | string
    promo_page?: {
      headline?: string
      subcopy?: string
    }
  }
  columns?: 'auto' | '1' | '2' | '3'
  showFilter?: boolean
  showSort?: boolean
  emptyMessage?: string
}

/*
  Renders the promo offers grouped under a beaker2 PROMO_PAGE step (BR-5419).
  Offers themselves are authored in beaker2 — this step only displays them.
*/
const PromoPageStep = ({
  step,
  columns = 'auto',
  showFilter = true,
  showSort = true,
  emptyMessage,
}: PromoPageStepProps) => {
  const { t } = useTranslation('promoPage')
  // DropdownSelect passes `title` back to onSelect as the menu's identity, so
  // the translated label doubles as the identity on both sides.
  const filterTitle = t('filterTitle')
  const sortTitle = t('sortTitle')
  const sortingOptions = useMemo(
    () => SORTING_PARAMS.map(({ labelKey, ...rest }) => ({
      ...rest,
      label: t(labelKey),
    })),
    [t],
  )
  // Enrichment exposes the beaker2 step_builder_step id as originalStepId;
  // step.id here would be the CMS step, which offers are not keyed on.
  const stepId = step?.originalStepId ?? step?.stepId

  const { offerTags } = useOfferTags()
  const [filterParams, setFilterParams] = useState<Record<string, string>>({})

  const { promoItems, isLoading } = usePromoItems(stepId, filterParams)

  const headline = step?.promo_page?.headline
  const subcopy = step?.promo_page?.subcopy

  const isWide =
    promoItems.length > 0 && promoItems.length <= WIDE_LAYOUT_MAX_ITEMS

  const handleSelect = useCallback((text: string, title: string) => {
    setFilterParams(prevParams => {
      const next = { ...prevParams }
      if (title === filterTitle) {
        if (text) {
          next[TAGS_PARAM] = text
        } else {
          delete next[TAGS_PARAM]
        }
      } else if (title === sortTitle) {
        if (text) {
          next[SORT_PARAM] = text
        } else {
          delete next[SORT_PARAM]
        }
      }
      return next
    })
  }, [filterTitle, sortTitle])

  // 'auto' keeps the original behaviour (wide 2-up for small sets, 3-up
  // otherwise); an explicit column count overrides the grid.
  const isFixedColumns = columns !== 'auto'
  // Cards are a fixed width, so without justifyItems they sit flush-left in
  // their track — most obvious at 1 column, where the single 1fr track spans
  // the whole container. The class's `justify-content: center` can't help:
  // it centers the tracks, and the tracks already fill the row.
  const gridStyle = isFixedColumns
    ? {
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        justifyItems: 'center' as const,
      }
    : undefined
  const useWideCards = isFixedColumns ? Number(columns) <= 2 : isWide

  return (
    <section id="offers-container" className={`step-content with-shadow ${styles.promoPage}`}>
      {headline && (
        <h2 className={styles.headline}>{headline}</h2>
      )}
      {subcopy && (
        <p className={styles.subcopy}>{subcopy}</p>
      )}
      {(showFilter || showSort) && (
        <div className={styles.selectbuttons}>
          {showFilter && (
            <DropdownSelect
              title={filterTitle}
              dropdownItems={offerTags}
              onSelect={handleSelect}
              singleSelect={false}
              clearSelect={t('clearFilters')}
            />
          )}
          {showSort && (
            <DropdownSelect
              title={sortTitle}
              dropdownItems={sortingOptions}
              onSelect={handleSelect}
            />
          )}
        </div>
      )}
      {!isLoading && promoItems.length === 0 ? (
        <p className={styles.emptyMessage}>
          {emptyMessage || t('emptyMessage')}
        </p>
      ) : (
        <div
          className={useWideCards ? styles.itemListFew : styles.itemList}
          style={gridStyle}
        >
          {promoItems.map(item => (
            <OfferCard
              key={item.id}
              {...item}
              wide={useWideCards}
              className={useWideCards ? styles.itemFew : styles.item}
            />
          ))}
        </div>
      )}
    </section>
  )
}

export default PromoPageStep
